/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Asymmetric SWOT Page - Portrait "No-Brainer" Layout
 *
 * Displays two SWOT grids in vertical stack:
 * 1. Status Quo (top) - Heavy on weaknesses/threats (the problem)
 * 2. Option A (bottom) - Heavy on strengths/opportunities (the solution)
 *
 * Design philosophy:
 * - Visual contrast creates "No-Brainer" feeling
 * - Dynamic height based on node impact weights
 * - Portrait orientation for natural reading flow (top to bottom)
 */

import type { SwotPageData, SwotQuadrantData, SwotNodeData } from '@core/presentationPrint';

interface AsymmetricSwotPageProps {
  data: SwotPageData;
}

interface QuadrantProps {
  label: string;
  nodes: SwotNodeData[];
  colorClass: string;
  totalImpact: number;
}

/**
 * Calculate dynamic height for a quadrant based on node impact
 * @param nodes - Array of nodes in the quadrant
 * @param totalImpact - Total impact across all quadrants for normalization
 * @returns Height in pixels (minimum 50px, scaled by impact weight * 200px)
 */
function calculateQuadrantHeight(nodes: SwotNodeData[], totalImpact: number): number {
  if (totalImpact === 0) return 50; // Minimum height
  const nodeImpactSum = nodes.reduce((sum, node) => sum + node.impactWeight, 0);
  // Scale factor: 200px distributes impact weight across quadrants
  return Math.max(50, (nodeImpactSum / totalImpact) * 200);
}

/**
 * Render a single SWOT quadrant with nodes
 */
function SwotQuadrant({ label, nodes, colorClass, totalImpact }: QuadrantProps) {
  const height = calculateQuadrantHeight(nodes, totalImpact);

  return (
    <div 
      className={`swot-quadrant swot-quadrant--${colorClass}`}
      style={{ minHeight: `${height}px` }}
    >
      <h4 className="swot-quadrant__title">{label}</h4>
      <div className="swot-quadrant__nodes">
        {nodes.map((node) => (
          <div
            key={node.id}
            className="swot-node"
            style={{ 
              // Font size scales with impact weight: min 10px, max 14px (impactWeight * 10px)
              fontSize: `${Math.max(10, Math.min(14, node.impactWeight * 10))}px`,
            }}
          >
            {node.icon && <span className="swot-node__icon">{node.icon}</span>}
            <span className="swot-node__label">{node.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Render a complete SWOT grid (4 quadrants)
 */
function SwotGrid({ 
  label, 
  quadrants 
}: { 
  label: string; 
  quadrants: SwotQuadrantData;
}) {
  // Calculate total impact across all quadrants for dynamic sizing
  const totalImpact = [
    ...quadrants.strengths,
    ...quadrants.weaknesses,
    ...quadrants.opportunities,
    ...quadrants.threats,
  ].reduce((sum, node) => sum + node.impactWeight, 0);

  return (
    <div className="swot-grid">
      <h3 className="swot-grid__label">{label}</h3>
      <div className="swot-grid__quadrants">
        <div className="swot-grid__row">
          <SwotQuadrant
            label="What Works"
            nodes={quadrants.strengths}
            colorClass="green"
            totalImpact={totalImpact}
          />
          <SwotQuadrant
            label="Holding it Back"
            nodes={quadrants.weaknesses}
            colorClass="red"
            totalImpact={totalImpact}
          />
        </div>
        <div className="swot-grid__row">
          <SwotQuadrant
            label="Opportunities"
            nodes={quadrants.opportunities}
            colorClass="blue"
            totalImpact={totalImpact}
          />
          <SwotQuadrant
            label="Threats"
            nodes={quadrants.threats}
            colorClass="amber"
            totalImpact={totalImpact}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Main Asymmetric SWOT Page component
 */
export function AsymmetricSwotPage({ data }: AsymmetricSwotPageProps) {
  return (
    <div className="dp-page dp-swot-page">
      <div className="dp-swot-header">
        <h2>{data.title}</h2>
        <p className="dp-swot-subtitle">
          Visual comparison showing the contrast between your current system and the recommended option
        </p>
      </div>

      {/* Status Quo Grid - The Problem (heavy on weaknesses) */}
      <div className="dp-swot-section dp-swot-section--problem">
        <SwotGrid
          label={data.statusQuo.label}
          quadrants={data.statusQuo.quadrants}
        />
      </div>

      {/* Visual separator */}
      <div className="dp-swot-arrow">
        <div className="dp-swot-arrow__icon">↓</div>
        <div className="dp-swot-arrow__text">Recommended Upgrade</div>
      </div>

      {/* Option A Grid - The Solution (heavy on strengths) */}
      <div className="dp-swot-section dp-swot-section--solution">
        <SwotGrid
          label={data.optionA.label}
          quadrants={data.optionA.quadrants}
        />
      </div>

      {/* Styling for this page */}
      <style>{`
        .dp-swot-page {
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .dp-swot-header {
          margin-bottom: 30px;
          text-align: center;
        }

        .dp-swot-header h2 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #1e293b;
        }

        .dp-swot-subtitle {
          font-size: 14px;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
        }

        .dp-swot-section {
          margin-bottom: 20px;
        }

        .dp-swot-section--problem {
          border: 2px solid #fca5a5;
          border-radius: 12px;
          padding: 16px;
          background: #fef2f2;
        }

        .dp-swot-section--solution {
          border: 2px solid #86efac;
          border-radius: 12px;
          padding: 16px;
          background: #f0fdf4;
        }

        .swot-grid {
          width: 100%;
        }

        .swot-grid__label {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 16px;
          color: #0f172a;
        }

        .swot-grid__quadrants {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .swot-grid__row {
          display: flex;
          gap: 8px;
        }

        .swot-quadrant {
          flex: 1;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .swot-quadrant--green {
          background: #dcfce7;
          border-color: #86efac;
        }

        .swot-quadrant--red {
          background: #fee2e2;
          border-color: #fca5a5;
        }

        .swot-quadrant--blue {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .swot-quadrant--amber {
          background: #fed7aa;
          border-color: #fdba74;
        }

        .swot-quadrant__title {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 10px;
          color: #0f172a;
        }

        .swot-quadrant__nodes {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .swot-node {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 10px;
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          font-size: 12px;
          font-weight: 500;
          color: #334155;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .swot-node__icon {
          font-size: 16px;
        }

        .swot-node__label {
          white-space: nowrap;
        }

        .dp-swot-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin: 20px 0;
          padding: 12px;
        }

        .dp-swot-arrow__icon {
          font-size: 32px;
          color: #0284c7;
          font-weight: bold;
        }

        .dp-swot-arrow__text {
          font-size: 16px;
          font-weight: 600;
          color: #0284c7;
        }

        /* Print styles */
        @media print {
          .dp-swot-page {
            page-break-before: always;
            page-break-after: always;
          }

          .dp-swot-section--problem,
          .dp-swot-section--solution {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}

export default AsymmetricSwotPage;
